import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import TCProductSelectStore, {
    BaseDevice,
    ProductSelectActions,
    ProductSelectCounterTypes,
} from './tc.product.select.store';
import { useStore, waitForStore } from '@tk/utilities/tk.use.store';

export default class TCDeviceQuantityCounter extends TKCustomElementFactory {
    store?: TCProductSelectStore;
    protected unsubscribe?: () => void;

    counterTypeAttribute = 'data-tc-counter-type';
    counterType: ProductSelectCounterTypes;

    deviceSelector = '[data-tc-device]';
    decreaserSelector = '[data-tk-counter-decreaser]';
    inputSelector = '[data-tc-device-quantity]';
    increaserSelector = '[data-tk-counter-increaser]';
    priceSelector = '[data-tc-device-price]';
    decreaser: HTMLElement | null;
    input: HTMLInputElement | null;
    increaser: HTMLElement | null;
    minValue: 1 | 0;
    deviceId: string;

    constructor() {
        super();
        this.counterType = this.getAttribute(this.counterTypeAttribute) as ProductSelectCounterTypes;
        this.decreaser = this.querySelector(this.decreaserSelector);
        this.input = this.querySelector(this.inputSelector);
        this.increaser = this.querySelector(this.increaserSelector);
        this.minValue = this.counterType === ProductSelectCounterTypes.BASE ? 1 : 0;
        this.deviceId = '';
    }

    async connectedCallback() {
        if (!this.decreaser) throw new Error(`Missing decreaser element.`);
        if (!this.input) throw new Error(`Missing input element.`);
        if (!this.increaser) throw new Error(`Missing increaser element.`);
        this.deviceId = this.getDeviceId();

        await waitForStore('productSelectStore');
        this.store = useStore('productSelectStore') as TCProductSelectStore;
        this.initializeStore();
        this.registerClickListener();
        this.registerChangeListener();
    }

    disconnectedCallback(): void {
        this.unsubscribe && this.unsubscribe();
    }

    initializeStore() {
        if (!this.store) return;
        this.unsubscribe = this.store.subscribe(({ action }) => this.render(action as ProductSelectActions));
    }

    getDeviceId(): string {
        const deviceWrapper = this.closest(this.deviceSelector);
        if (!deviceWrapper) throw new Error('Missing device wrapper.');
        if (!deviceWrapper.id) throw new Error('Missing device id.');
        return deviceWrapper.id;
    }

    registerClickListener() {
        this.pushListener({
            event: 'click',
            element: this,
            action: this.handleClick.bind(this),
        });
    }

    handleClick(event: Event) {
        const target = event.target as HTMLElement;
        if (!this.input) return;

        let inputValue = parseFloat(this.input.value);
        if (isNaN(inputValue)) inputValue = 0;

        if (target.closest(this.decreaserSelector)) inputValue > this.minValue && (inputValue -= 1);
        else if (target.closest(this.increaserSelector)) inputValue += 1;
        else return;

        inputValue = Math.max(inputValue, this.minValue);
        if (this.input.value === inputValue.toString()) return;
        this.input.value = inputValue.toString();
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    registerChangeListener() {
        this.pushListener({
            event: 'change',
            element: this.input!,
            action: this.handleChange.bind(this),
        });
    }

    handleChange() {
        if (!this.input) return;

        let inputValue = parseFloat(this.input.value);
        inputValue = isNaN(inputValue) ? 0 : Math.floor(inputValue);
        inputValue = Math.max(inputValue, this.minValue);
        this.input.value = inputValue.toString();
        this.checkMinValue(inputValue);

        this.store?.setDeviceQuantity(this.deviceId, inputValue, this.counterType);
    }

    checkMinValue(value: number) {
        if (!this.decreaser || isNaN(this.minValue) || isNaN(value)) return;
        value <= this.minValue
            ? this.decreaser.setAttribute('disabled', 'true')
            : this.decreaser.removeAttribute('disabled');
    }

    render(action: ProductSelectActions) {
        const device = this.store!.getDevice(this.deviceId) as BaseDevice;
        if (
            (this.counterType === ProductSelectCounterTypes.BASE &&
                action === ProductSelectActions.SET_BASE_QUANTITY) ||
            (this.counterType === ProductSelectCounterTypes.COUNTERPART &&
                (action === ProductSelectActions.SET_COUNTERPART_QUANTITY ||
                    action === ProductSelectActions.SET_COUNTERPART_ACTIVE ||
                    action === ProductSelectActions.SET_COUNTERPART_INACTIVE))
        )
            this.refreshQuantity(device);
        if (
            action === ProductSelectActions.SET_BASE_QUANTITY ||
            action === ProductSelectActions.SET_COUNTERPART_QUANTITY ||
            action === ProductSelectActions.SET_COUNTERPART_ACTIVE ||
            action === ProductSelectActions.SET_COUNTERPART_INACTIVE
        )
            this.refreshPrice(device);
    }

    refreshQuantity(device: BaseDevice) {
        const quantity = device?.quantity.toString();
        if (!quantity) return;
        if (this.input!.value !== quantity) {
            this.input!.value = quantity;
            this.checkMinValue(device.quantity);
        }
    }

    refreshPrice(device: BaseDevice) {
        const deviceWrapper = this.closest(this.deviceSelector);
        const priceWrapper = deviceWrapper?.querySelector(this.priceSelector);
        if (!priceWrapper) return;
        if (!this.store?.getLoginStatus()) {
            priceWrapper && (priceWrapper.textContent = '');
            return;
        }
        const price = TCDeviceQuantityCounter.calculateTotalPrice(device.price, device.quantity);
        priceWrapper.textContent = price;
    }

    static calculateTotalPrice(price: string, quantity: number): string {
        const numericPrice = parseFloat(price.replace(/'/g, ''));
        if (price === '-' || isNaN(numericPrice) || numericPrice === 0) return '-';

        const total = quantity > 0 ? numericPrice * quantity : numericPrice * 1;
        return TCDeviceQuantityCounter.formatPriceWithApostroph(total);
    }

    static formatPriceWithApostroph(amount: number): string {
        return amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    }
}
